import React, { useState, useEffect } from 'react';
import { FaPlus, FaExclamationTriangle } from 'react-icons/fa';
import RiskDetailsPanel from './RiskDetailsPanel';
import { apiFetch } from '../../../utils/apiAdmin';
import { apiFetch as clientApiFetch } from '../../../utils/apiClient';

const RisksTable = ({
    risks,
    members,
    onRiskClick,
    onRefresh,
    leadId,
    isAdmin = false,
    preventRefreshRef,
    currentUserEmail,
    hideHeader = false,
    showCreate = false,
    onShowCreateChange
}) => {
    const [selectedRisk, setSelectedRisk] = useState(null);
    const [showCreatePanel, setShowCreatePanel] = useState(false);

    // Handle controlled create mode from parent
    useEffect(() => {
        if (showCreate) {
            setShowCreatePanel(true);
        }
    }, [showCreate]);

    const handleRiskClick = (risk) => {
        setSelectedRisk(risk);
        if (onRiskClick) onRiskClick(risk);
    };

    const handleClosePanel = () => {
        setSelectedRisk(null);
        setShowCreatePanel(false);
        if (onShowCreateChange) onShowCreateChange(false);
    };

    const handleSave = async (riskData) => {
        const api = isAdmin ? apiFetch : clientApiFetch;
        const baseUrl = process.env.REACT_APP_API_URL;

        try {
            if (selectedRisk) {
                // Update existing risk
                const response = await api(
                    `${baseUrl}/admin/risks/${leadId}/${selectedRisk.riskId}`,
                    {
                        method: 'PUT',
                        body: JSON.stringify(riskData)
                    }
                );
                if (!response.ok) throw new Error('Failed to update risk');
                const result = await response.json();
                // Keep panel open, update selected risk with saved data
                setSelectedRisk(result.risk);
            } else {
                // Create new risk
                const response = await api(
                    `${baseUrl}/admin/risks/${leadId}`,
                    {
                        method: 'POST',
                        body: JSON.stringify(riskData)
                    }
                );
                if (!response.ok) throw new Error('Failed to create risk');
                // Close panel after creating new risk
                handleClosePanel();
            }

            // Silent refresh to update table (no loading spinner)
            if (onRefresh) onRefresh(false);
        } catch (err) {
            console.error('Error saving risk:', err);
            alert('Failed to save risk: ' + err.message);
        }
    };

    const handleDelete = async (riskId) => {
        if (!window.confirm('Are you sure you want to delete this risk?')) return;

        const api = isAdmin ? apiFetch : clientApiFetch;
        const baseUrl = process.env.REACT_APP_API_URL;

        try {
            const response = await api(
                `${baseUrl}/admin/risks/${leadId}/${riskId}`,
                { method: 'DELETE' }
            );
            if (!response.ok) throw new Error('Failed to delete risk');

            handleClosePanel();
            // Silent refresh to update table (no loading spinner)
            if (onRefresh) onRefresh(false);
        } catch (err) {
            console.error('Error deleting risk:', err);
            alert('Failed to delete risk: ' + err.message);
        }
    };

    const handleRemoveSignOff = async (riskId, reason) => {
        const api = isAdmin ? apiFetch : clientApiFetch;
        const baseUrl = process.env.REACT_APP_API_URL;

        try {
            const response = await api(
                `${baseUrl}/admin/risks/${leadId}/${riskId}/remove-signoff`,
                {
                    method: 'POST',
                    body: JSON.stringify({ reason })
                }
            );
            if (!response.ok) throw new Error('Failed to remove sign-off');

            const result = await response.json();
            // Keep panel open, update selected risk with updated data
            if (result.risk) {
                setSelectedRisk(result.risk);
            }

            // Silent refresh to update table (no loading spinner)
            if (onRefresh) onRefresh(false);
        } catch (err) {
            console.error('Error removing sign-off:', err);
            alert('Failed to remove sign-off: ' + err.message);
        }
    };

    const handleSignOff = async (riskId, acknowledgement) => {
        const baseUrl = process.env.REACT_APP_API_URL;

        // Prevent WebSocket refresh during our own update
        if (preventRefreshRef) preventRefreshRef.current = true;

        try {
            const response = await clientApiFetch(
                `${baseUrl}/client/risks/${riskId}/sign-off`,
                {
                    method: 'POST',
                    body: JSON.stringify({ acknowledgement })
                }
            );
            if (!response.ok) {
                let serverError = 'Failed to sign off';
                try {
                    const data = await response.json();
                    if (data.error) serverError = data.error;
                } catch (parseErr) { /* non-JSON response body */ }
                throw new Error(serverError);
            }

            const result = await response.json();
            // Keep panel open, update selected risk with signed-off data
            if (result.risk) {
                setSelectedRisk(result.risk);
            }

            // Silent refresh to update table (no loading spinner)
            if (onRefresh) onRefresh(false);
        } catch (err) {
            console.error('Error signing off:', err);
            alert('Failed to sign off: ' + err.message);
        } finally {
            // Re-enable WebSocket refresh after a delay (allow notification to pass)
            setTimeout(() => {
                if (preventRefreshRef) preventRefreshRef.current = false;
            }, 2000);
        }
    };

    const getStatusBadge = (risk) => {
        // Status derived from sign-off state
        if (risk.signOff) {
            return <span className="text-sm text-green-700">Signed Off</span>;
        }
        return <span className="text-sm text-amber-700">Awaiting Sign-off</span>;
    };

    const getImpactBadge = (impact) => {
        const impactClasses = {
            'High': 'bg-red-100 text-red-800',
            'Medium': 'bg-orange-100 text-orange-800',
            'Low': 'bg-blue-100 text-blue-800'
        };
        return impact ? (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${impactClasses[impact] || 'bg-gray-100 text-gray-800'}`}>
                {impact}
            </span>
        ) : (
            <span className="text-gray-400 text-xs">-</span>
        );
    };

    const getSignOffStatus = (risk) => {
        if (risk.signOff) {
            return <span className="text-xs text-green-700">Signed</span>;
        }
        if (risk.assignedToEmail) {
            return <span className="text-xs text-amber-700">Pending</span>;
        }
        return <span className="text-xs text-gray-400">-</span>;
    };

    return (
        <div className={`h-full flex flex-col ${hideHeader ? '' : 'bg-white rounded-lg shadow'}`}>
            {/* Header */}
            {!hideHeader && (
                <div className="flex items-center justify-between px-4 py-3 border-b">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold">Risk Register</h2>
                        <span className="text-sm text-gray-500">({risks.length})</span>
                    </div>
                    {isAdmin && (
                        <button
                            onClick={() => setShowCreatePanel(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                        >
                            <FaPlus size={12} />
                            Add Risk
                        </button>
                    )}
                </div>
            )}

            {/* Table */}
            <div className="flex-1 overflow-auto">
                {risks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                        <FaExclamationTriangle size={48} className="text-gray-300 mb-4" />
                        <p>No risks have been identified yet.</p>
                        {isAdmin && (
                            <button
                                onClick={() => setShowCreatePanel(true)}
                                className="mt-4 text-primary-600 hover:text-primary-700"
                            >
                                Add the first risk
                            </button>
                        )}
                    </div>
                ) : (
                    <table className="w-full table-fixed">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th style={{width: '10%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                                <th style={{width: '35%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                                <th style={{width: '15%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th style={{width: '10%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Impact</th>
                                <th style={{width: '22%'}} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sign-off Client</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {risks.map(risk => (
                                <tr
                                    key={risk.riskId}
                                    onClick={() => handleRiskClick(risk)}
                                    className="hover:bg-gray-50 cursor-pointer"
                                >
                                    <td className="px-4 py-3 text-sm font-mono font-medium text-gray-900">
                                        {risk.riskId}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                        {risk.title}
                                    </td>
                                    <td className="px-4 py-3">
                                        {getStatusBadge(risk)}
                                    </td>
                                    <td className="px-4 py-3">
                                        {getImpactBadge(risk.impact)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                        {risk.assignedToName || '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Risk Details Panel */}
            {(selectedRisk || showCreatePanel) && (
                <RiskDetailsPanel
                    risk={selectedRisk}
                    members={members}
                    isAdmin={isAdmin}
                    currentUserEmail={currentUserEmail}
                    onClose={handleClosePanel}
                    onSave={handleSave}
                    onDelete={handleDelete}
                    onRemoveSignOff={handleRemoveSignOff}
                    onSignOff={handleSignOff}
                />
            )}
        </div>
    );
};

export default RisksTable;
